import { Table } from "@/components/elements/Table";
import { TIndustrySubject } from "@/api/services/main/company/dtos/get-company-list.dto";
import { useTable } from "@/hooks/useTable";
import { divider } from "@/theme/colors";
import { dayjs } from "@/utils/dayjs";
import { Box } from "@mui/material";
import { Dayjs } from "dayjs";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo } from "react";
import { DATE_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";

type TCurriculumScheduleRow = {
  id: string;
  weekNumber: number;
  startDate: string;
  endDate: string;
  weekName: string;
  vocabularyCount: number | null;
  questionCount: number | null;
};

type TCurriculumScheduleTableProps = {
  learningStartDate?: Dayjs | null;
  subjects: TIndustrySubject[];
  mockExamQuestionCount?: number | null;
  /** Table height. Left undefined here so the table grows naturally; consumers (e.g. the create-plan modal) pass an explicit value when they need a scrollable, bounded height. */
  heightTable?: string;
};

const columns: MRT_ColumnDef<TCurriculumScheduleRow>[] = [
  {
    id: "weekNumber",
    header: "週数",
    accessorKey: "weekNumber",
    size: 60,
    Header: () => <Box pl={1}>週数</Box>,
    Cell: ({ row }) => <Box pl={1}>{row.original.weekNumber}</Box>,
  },
  {
    id: "startDate",
    header: "学習開始日",
    accessorKey: "startDate",
    size: 106,
  },
  {
    id: "endDate",
    header: "想定期日",
    accessorKey: "endDate",
    size: 106,
  },
  {
    id: "weekName",
    header: "WEEK名",
    accessorKey: "weekName",
    size: 420,
  },
  {
    id: "vocabularyCount",
    header: "単語数",
    accessorKey: "vocabularyCount",
    size: 74,
    Cell: ({ row }) => row.original.vocabularyCount ?? EMPTY_STR.TEXT,
  },
  {
    id: "questionCount",
    header: "問題数",
    accessorKey: "questionCount",
    size: 74,
    Cell: ({ row }) => row.original.questionCount ?? EMPTY_STR.TEXT,
  },
];

export const CurriculumScheduleTable: FC<TCurriculumScheduleTableProps> = ({
  learningStartDate,
  subjects,
  mockExamQuestionCount,
  heightTable,
}) => {
  const rows = useMemo<TCurriculumScheduleRow[]>(() => {
    if (!subjects.length) return [];

    const start = learningStartDate && dayjs(learningStartDate).isValid() ? dayjs(learningStartDate) : null;
    const subjectRows: TCurriculumScheduleRow[] = subjects.map((subject, index) => {
      const weekStart = start ? start.add(index * 7, "day") : null;
      const weekEnd = weekStart ? weekStart.add(6, "day") : null;
      return {
        id: `subject-${subject.id}`,
        weekNumber: index + 1,
        startDate: weekStart ? weekStart.format(DATE_FORMAT) : EMPTY_STR.TEXT,
        endDate: weekEnd ? weekEnd.format(DATE_FORMAT) : EMPTY_STR.TEXT,
        weekName: subject.name,
        vocabularyCount: subject.vocabularyCount ?? null,
        questionCount: subject.questionCount ?? null,
      };
    });

    const mockWeekStart = start ? start.add(subjects.length * 7, "day") : null;
    subjectRows.push({
      id: "mock-exam",
      weekNumber: subjects.length + 1,
      startDate: mockWeekStart ? mockWeekStart.format(DATE_FORMAT) : EMPTY_STR.TEXT,
      endDate: start ? "試験日まで" : EMPTY_STR.TEXT,
      weekName: "模擬試験実施",
      vocabularyCount: null,
      questionCount: mockExamQuestionCount != null && mockExamQuestionCount > 0 ? mockExamQuestionCount : null,
    });

    return subjectRows;
  }, [learningStartDate, subjects, mockExamQuestionCount]);

  const { table } = useTable({
    columns,
    rows,
    getRowId: (row) => row.id,
    renderEmptyRowsFallback: () => <Box />,
  });

  return <Table table={table} height={heightTable} headRowHeight={37} bodyRowHeight={42} borderColor={divider.middle} />;
};
